import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

export const scopeSchema = z.enum([
  "franklin.read",
  "franklin.write",
  "franklin.share",
  "franklin.admin",
  "franklin.sign",
  "franklin.billing",
]);
export type ConnectorScope = z.infer<typeof scopeSchema>;

function plainOrigin(url: URL): boolean {
  return !url.username && !url.password && !url.search && !url.hash && url.pathname === "/";
}

function permittedScheme(url: URL): boolean {
  if (url.protocol === "https:") return true;
  if (url.protocol !== "http:") return false;
  const loopbacks = ["localhost", "127.0.0.1", "[::1]"];
  return loopbacks.includes(url.hostname) && loopbacks.includes(window.location.hostname);
}

export function connectorOrigin(): string | null {
  const configured = import.meta.env.VITE_CONNECTOR_ORIGIN;
  if (!configured) return null;
  try {
    const url = new URL(configured);
    return plainOrigin(url) && permittedScheme(url) ? url.origin : null;
  } catch {
    return null;
  }
}

export class ConnectorError extends Error {
  constructor(readonly status: number) {
    super("connector_request_failed");
  }
}

async function send<T>(
  destination: URL,
  init: NonNullable<Parameters<typeof fetch>[1]>,
  schema: z.ZodType<T>
): Promise<T> {
  const origin = connectorOrigin();
  if (!origin) throw new ConnectorError(503);
  const allowedOrigins = [origin];
  if (allowedOrigins.includes(destination.origin)) {
    const outgoing = new Request(destination, {
      ...init,
      cache: "no-store",
      credentials: "omit",
      redirect: "error",
      referrerPolicy: "no-referrer",
    });
    const response = await fetch(outgoing);
    if (!response.ok) throw new ConnectorError(response.status);
    return schema.parse(await response.json());
  }
  throw new ConnectorError(400);
}

async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const origin = connectorOrigin();
  if (!origin) throw new ConnectorError(503);
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) throw new ConnectorError(401);
  return send(
    new URL(path, origin),
    {
      method: body === undefined ? "GET" : "POST",
      headers: {
        Authorization: `Bearer ${data.session.access_token}`,
        "Content-Type": "application/json",
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal,
    },
    schema
  );
}

const detailsSchema = z.object({
  client: z.object({ client_id: z.string(), client_name: z.string().nullish() }),
  organizations: z.array(z.object({ id: z.string(), name: z.string() })),
  scopes: z.array(scopeSchema),
  offline_access: z.boolean(),
});
export type ConsentDetails = z.infer<typeof detailsSchema>;
export const loadConsent = (oauthQuery: string, signal?: AbortSignal) =>
  request("/authorize/details", detailsSchema, { oauth_query: oauthQuery }, signal);

type Consent =
  | { accept: false; oauth_query: string }
  | {
      accept: true;
      oauth_query: string;
      organization: string;
      scopes: ConnectorScope[];
    };
export const submitConsent = (consent: Consent) =>
  request("/authorize/consent", z.object({ redirect_uri: z.string().url() }), consent);

export function completeConsent(uri: string): void {
  const url = new URL(uri);
  if (
    url.protocol !== "https:" &&
    !(url.protocol === "http:" && ["127.0.0.1", "localhost", "[::1]"].includes(url.hostname))
  ) {
    throw new ConnectorError(400);
  }
  window.location.assign(url.href);
}

const connectionSchema = z.object({
  id: z.string().uuid(),
  client_name: z.string().nullable(),
  organization: z.string(),
  organization_name: z.string().nullable(),
  scopes: z.array(scopeSchema),
  created_at: z.string().datetime(),
  last_used_at: z.string().datetime().nullable(),
  revoked_at: z.string().datetime().nullable(),
});
export type Connection = z.infer<typeof connectionSchema>;
const pageSchema = z.object({
  connections: z.array(connectionSchema),
  next_cursor: z.string().uuid().nullable(),
});
export const listConnections = (cursor: string | null, signal?: AbortSignal) =>
  request(
    `/connections${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    pageSchema,
    undefined,
    signal
  );
export const revokeConnection = (id: string) =>
  request(
    `/connections/${encodeURIComponent(z.string().uuid().parse(id))}/revoke`,
    z.object({ revoked: z.literal(true) }),
    {}
  );

export const uploadSchema = z.object({
  upload_id: z.string().uuid(),
  state: z.enum(["pending", "uploaded", "completed", "cancelled", "expired"]),
  filename: z.string(),
  size: z.number(),
  uploaded: z.number(),
  content_type: z.string(),
  document_id: z.number().nullable(),
  expires_at: z.string(),
  destination: z.object({
    building_name: z.string(),
    project_name: z.string(),
    folder_name: z.string(),
    folder_path: z.string().nullable(),
  }),
  upload_url: z.string().url(),
  upload_token: z.string().nullable(),
  chunk_size: z.number(),
});
export type ConnectorUpload = z.infer<typeof uploadSchema>;
export const loadConnectorUpload = (id: string, signal?: AbortSignal) =>
  request(
    `/uploads/${encodeURIComponent(z.string().uuid().parse(id))}`,
    uploadSchema,
    undefined,
    signal
  );

export function transferUrl(upload: ConnectorUpload): string {
  const origin = connectorOrigin();
  const expected = `${origin}/transfers/${z.string().uuid().parse(upload.upload_id)}`;
  if (!origin || upload.upload_url !== expected) throw new ConnectorError(400);
  return expected;
}

export async function finishConnectorUpload(
  upload: ConnectorUpload,
  cancel = false
): Promise<ConnectorUpload> {
  return send(
    new URL(transferUrl(upload)),
    {
      method: cancel ? "DELETE" : "POST",
      headers: { Authorization: `Bearer ${upload.upload_token}` },
    },
    uploadSchema
  );
}

const actionSchema = z.object({
  id: z.string().uuid(),
  organization: z.string(),
  organization_name: z.string().nullable(),
  client_id: z.string(),
  client_name: z.string().nullable(),
  tool_name: z.string(),
  arguments: z.record(z.unknown()),
  preview: z.record(z.unknown()),
  digest: z.string().regex(/^[a-f0-9]{64}$/),
  state: z.enum([
    "pending",
    "approved",
    "denied",
    "cancelled",
    "succeeded",
    "conflicted",
    "expired",
  ]),
  expires_at: z.string().datetime(),
});
export type ConnectorAction = z.infer<typeof actionSchema>;
export const loadConnectorAction = (id: string, signal?: AbortSignal) =>
  request(
    `/actions/${encodeURIComponent(z.string().uuid().parse(id))}`,
    actionSchema,
    undefined,
    signal
  );
export const decideConnectorAction = (id: string, digest: string, accept: boolean) =>
  request(
    `/actions/${encodeURIComponent(z.string().uuid().parse(id))}/decision`,
    z.object({ state: z.enum(["approved", "denied"]) }),
    { digest, accept }
  );
